import {
  ContractInvoiceType,
  ContractStatus,
} from "@boark/shared/types/contract";
import { ContractWithCounterParty } from "@boark/shared/types/contract-with-counter-party";
import { useMemo } from "react";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetContracts } from "../../queries/contract";
import { useGetOrganizations } from "../../queries/organization";
import { uniqueSortedTagSuggestions } from "../../services/contract";
import { formatOrganizationLabel } from "../../services/organization";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { formatPrice } from "../../utils/format-price";
import { recordIdToId } from "../../utils/record-id-to-id";
import { Contract } from "./contract";
import { ContractCreate } from "./contract-create";
import { ContractDelete } from "./contract-delete";
import { ContractEdit } from "./contract-edit";

export function ContractList() {
  const { locale: t, currentLanguage, get: getLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: contracts, isLoading: isLoadingContracts } = useGetContracts();
  const { data: organizations } = useGetOrganizations();

  const tagFilterOptions = useMemo(
    () =>
      uniqueSortedTagSuggestions(contracts).map((tag) => ({
        label: tag,
        value: tag,
      })),
    [contracts],
  );

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  function statusLabel(status: ContractStatus) {
    return status === ContractStatus.ACTIVE
      ? t.contract.statusActive
      : t.contract.statusInactive;
  }

  function invoiceTypeLabel(invoiceType: ContractInvoiceType) {
    switch (invoiceType) {
      case ContractInvoiceType.MONTHLY:
        return t.contract.invoiceTypeMonthly;
      case ContractInvoiceType.QUARTERLY:
        return t.contract.invoiceTypeQuarterly;
      case ContractInvoiceType.SEMI_ANNUALLY:
        return t.contract.invoiceTypeSemiAnnually;
      case ContractInvoiceType.ANNUALLY:
        return t.contract.invoiceTypeAnnually;
    }
  }

  return (
    <>
      <Routes>
        <Route element={<Contract />} path="/:id" />
        <Route element={<ContractCreate />} path="/create" />
        <Route element={<ContractEdit />} path="/:id/edit" />
        <Route element={<ContractDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.contract.contracts}</H1>
          <Button route="/admin/contract/list/create">
            {t.contract.create}
          </Button>
        </div>
        <Table
          loading={isLoadingContracts}
          data={contracts || []}
          editRouteButton={(row) =>
            `/admin/contract/list/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/contract/list/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "title"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          viewRouteButton={(row) =>
            `/admin/contract/list/${recordIdToId(row.id)}`
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="title">{t.contract.title}</Column>
          <Column
            body={(row: ContractWithCounterParty) => statusLabel(row.status)}
            field="status"
            filterableBy="select"
            filterOptions={() => [
              {
                label: t.contract.statusActive,
                value: ContractStatus.ACTIVE,
              },
              {
                label: t.contract.statusInactive,
                value: ContractStatus.INACTIVE,
              },
            ]}
          >
            {t.contract.status}
          </Column>
          <Column
            body={(row: ContractWithCounterParty) => {
              if (!row.cost) return "-";
              const value = formatPrice(row.cost, currentLanguage, "SEK");
              return row.invoiceType
                ? `${value} / ${invoiceTypeLabel(row.invoiceType)}`
                : value;
            }}
            field="cost"
            filterableBy="text"
          >
            {t.contract.cost}
          </Column>
          <Column
            body={(row: ContractWithCounterParty) =>
              row.counterParty
                ? formatOrganizationLabel(row.counterParty)
                : "-"
            }
            field="counterParty.id"
            filterableBy="select"
            filterOptions={(data: ContractWithCounterParty[]) =>
              organizations
                ?.filter((organization) =>
                  data.some(
                    (contract) =>
                      contract.counterParty?.id === organization.id,
                  ),
                )
                .map((organization) => ({
                  label: formatOrganizationLabel(organization),
                  value: organization.id,
                })) ?? []
            }
          >
            {t.contract.counterParty}
          </Column>
          <Column
            body={(row: ContractWithCounterParty) => formatDate(row.fromDate)}
            field="fromDate"
            filterableBy="date"
          >
            {getLocale("contract.fromDate")}
          </Column>
          <Column
            body={(row: ContractWithCounterParty) =>
              row.toDate ? formatDate(row.toDate) : "-"
            }
            field="toDate"
            filterableBy="date"
          >
            {getLocale("contract.toDate")}
          </Column>
          <Column
            body={(row: ContractWithCounterParty) =>
              row.tags.length > 0 ? row.tags.join(", ") : "-"
            }
            field="tags"
            filterableBy="select"
            filterOptions={() => tagFilterOptions}
          >
            {t.contract.tags}
          </Column>
        </Table>
      </div>
    </>
  );
}
